import { addSessionEvent } from '@/db/session-events';
import type { RoundId, SessionId, UserId } from '@/schemas';
import { createOperationId } from '../../operation-queue';
import { createSystemEvent } from '../../session-helpers';
import type { QueueDependencies } from '../queue-utils';

type StartRoundQueueParams = QueueDependencies & {
  sessionId: SessionId;
  roundId: RoundId;
  roundNumber: number;
  previousRoundNumber: number;
  name?: string;
  userId: UserId | undefined;
  timestamp: number;
};

type CompleteRoundQueueParams = QueueDependencies & {
  sessionId: SessionId;
  roundId: RoundId;
  previousCompletedAt: number | undefined;
  previousUpdatedAt: number;
  userId: UserId | undefined;
  timestamp: number;
};

export const enqueueStartRound = async ({
  operationQueue,
  set,
  refreshSessionFromEvents,
  updateUndoRedoAvailability,
  sessionId,
  roundId,
  roundNumber,
  previousRoundNumber,
  name,
  userId,
  timestamp,
}: StartRoundQueueParams): Promise<void> => {
  await operationQueue.enqueue<void>({
    id: createOperationId(),
    type: 'startRound',
    dedupKey: `round:start:${sessionId}:${roundId}`,
    priority: 9,
    optimisticUpdate: () => {
      set((draft) => {
        if (!draft.activeSession || draft.activeSession.id !== sessionId) {
          return;
        }
        draft.activeSession.rounds.push({
          id: roundId,
          sessionId,
          number: roundNumber,
          name,
          scores: {},
          notes: undefined,
          tags: undefined,
          imageIds: [],
          createdAt: timestamp,
          updatedAt: timestamp,
          deletedAt: undefined,
        });
        draft.activeSession.currentRoundNumber = roundNumber;
      });
    },
    rollback: async () => {
      set((draft) => {
        if (!draft.activeSession || draft.activeSession.id !== sessionId) {
          return;
        }
        draft.activeSession.rounds = draft.activeSession.rounds.filter(
          (round) => round.id !== roundId,
        );
        draft.activeSession.currentRoundNumber = previousRoundNumber;
      });
      await refreshSessionFromEvents(sessionId);
    },
    execute: async () => {
      await addSessionEvent(
        createSystemEvent(
          sessionId,
          'RoundStarted',
          {
            roundId,
            number: roundNumber,
            name,
          },
          userId,
        ),
      );
      await updateUndoRedoAvailability(sessionId);
    },
  });
};

export const enqueueCompleteRound = async ({
  operationQueue,
  set,
  refreshSessionFromEvents,
  updateUndoRedoAvailability,
  sessionId,
  roundId,
  previousCompletedAt,
  previousUpdatedAt,
  userId,
  timestamp,
}: CompleteRoundQueueParams): Promise<void> => {
  await operationQueue.enqueue<void>({
    id: createOperationId(),
    type: 'completeRound',
    dedupKey: `round:complete:${sessionId}:${roundId}`,
    priority: 8,
    optimisticUpdate: () => {
      set((draft) => {
        const round = draft.activeSession?.rounds.find((candidate) => candidate.id === roundId);
        if (round) {
          round.completedAt = timestamp;
          round.updatedAt = timestamp;
        }
      });
    },
    rollback: async () => {
      set((draft) => {
        const round = draft.activeSession?.rounds.find((candidate) => candidate.id === roundId);
        if (round) {
          round.completedAt = previousCompletedAt;
          round.updatedAt = previousUpdatedAt;
        }
      });
      await refreshSessionFromEvents(sessionId);
    },
    execute: async () => {
      await addSessionEvent(
        createSystemEvent(
          sessionId,
          'RoundCompleted',
          {
            roundId,
          },
          userId,
        ),
      );
      await updateUndoRedoAvailability(sessionId);
    },
  });
};
